/** Pieces several league tabs use */
import { getPicker, getTotalPicks, type DraftLeague, type DraftPlayer } from '@ogreclan/league-core';
import type { LeagueEvent } from '../api';

export function playerName(league: DraftLeague, id: string) {
	return league.players.find(p => p.id === id)?.name || id;
}

export function you(league: DraftLeague, youId: string | null) {
	return youId ? league.players.find(p => p.id === youId) || null : null;
}

export interface BoardSlot {
	/** Its place in the draft order, from 0 */
	n: number;
	player: DraftPlayer;
	species: string;
	skipped: boolean;
	/** For a skipped turn, the Pokémon they picked later to make up for it */
	late: string;
}

/** Every turn of the draft order, and what happened on it */
export function boardSlots(league: DraftLeague): BoardSlot[] {
	const slots: BoardSlot[] = [];
	for (let n = 0; n < getTotalPicks(league); n++) {
		const player = getPicker(league, n);
		if (player) slots.push({ n, player, species: '', skipped: false, late: '' });
	}
	let next = 0;
	for (const pick of league.picks) {
		if (pick.owed) {
			const slot = slots.find(s => s.skipped && !s.late && s.player.id === pick.player);
			if (slot) slot.late = pick.species;
			continue;
		}
		const slot = slots[next++];
		if (!slot) continue;
		if (pick.skipped) slot.skipped = true;
		else slot.species = pick.species;
	}
	return slots;
}

/** "picked Garchomp", from a logged command */
export function describeEvent(league: DraftLeague, event: LeagueEvent) {
	const who = event.actor ? playerName(league, event.actor) : 'The server';
	const args = event.detail.replace(/^\/draft \S+\s*/, '');
	switch (event.action) {
	case 'pick': return `${who} picked ${args}`;
	case 'forcepick': return `${who} made a pick for ${args.split(',')[0]}`;
	case 'captain': return `${who} changed Tera Captains`;
	case 'skip': return `${who} skipped the player on the clock`;
	case 'undo': return `${who} took back the last pick`;
	case 'start': return `${who} started the draft`;
	case 'end': return `${who} ended the draft`;
	case 'trade': return `${who} ${args.startsWith('approve') ? 'approved a trade' : args.startsWith('accept') ? 'accepted a trade' : 'made a trade offer'}`;
	case 'freeagent': return `${who} picked up a free agent`;
	case 'saveteam': return `${who} saved a team`;
	case 'report': return `${who} entered a result`;
	case 'update': return `The league was updated`;
	case 'delete': return `${who} deleted the league`;
	default: return `${who}: ${event.detail || event.action}`;
	}
}
